import { useEffect, useState } from 'react';
import { Languages } from 'lucide-react';

import { t, type UiLangCode } from '../lib/i18n';
import { LANG_OPTIONS } from '../lib/types';

interface ReceiptLanguageDialogProps {
  /** Interface language — drives this dialog's own copy. */
  lang: UiLangCode;
  /** Fires with the chosen OCR language; the caller then starts the scan. */
  onConfirm: (ocrLang: UiLangCode) => void;
}

/**
 * First-upload prompt: asks what language the receipts are in before the
 * first scan runs, instead of silently reusing the interface language.
 *
 * Why ask at all: the two are genuinely uncorrelated. Someone reading the
 * app in French may be scanning receipts from a trip to Portugal, and a
 * visitor who landed on /es/ from a Spanish search result gets a Spanish
 * UI regardless of what is on their receipts. Guessing wrong costs a full
 * re-OCR plus a ~12 MB language-pack download, so one question up front
 * is cheaper than the recovery.
 *
 * Shown once ever — the answer is persisted as the store's `ocrLang`.
 * Afterwards the language is changeable globally (settings card) and per
 * receipt (session card, which re-runs that receipt).
 *
 * Two steps rather than a bare dropdown: the common case is "same as the
 * interface", which collapses to a single click. The picker only appears
 * for people who say no, so the frequent path stays one tap.
 */
export function ReceiptLanguageDialog({ lang, onConfirm }: ReceiptLanguageDialogProps): JSX.Element {
  const [picking, setPicking] = useState(false);
  const [choice, setChoice] = useState<UiLangCode>(lang);

  const uiLangLabel = LANG_OPTIONS.find((o) => o.code === lang)?.label ?? lang;

  // Escape resolves to the interface language rather than trapping the
  // user: files are already waiting to be scanned, so the dialog must
  // always produce an answer. Same reasoning as DonationDialog treating
  // Escape as "proceed".
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onConfirm(lang);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onConfirm, lang]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="receipt-lang-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 px-4 dark:bg-slate-950/70"
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl dark:bg-slate-900">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400">
            <Languages className="h-5 w-5" aria-hidden="true" />
          </span>
          <h2
            id="receipt-lang-title"
            className="text-lg font-semibold text-slate-900 dark:text-slate-100"
          >
            {t(lang, 'receiptLang.title')}
          </h2>
        </div>
        <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">
          {t(lang, 'receiptLang.body')}
        </p>
        <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">
          {t(lang, 'language.firstUseDownload')}
        </p>

        {picking ? (
          <div className="mt-5 flex flex-col gap-3">
            <label htmlFor="receipt-lang-select" className="sr-only">
              {t(lang, 'receiptLang.cardLabel')}
            </label>
            <select
              id="receipt-lang-select"
              value={choice}
              autoFocus
              onChange={(e) => setChoice(e.target.value as UiLangCode)}
              className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-indigo-400 dark:focus:ring-indigo-400"
            >
              {LANG_OPTIONS.map((opt) => (
                <option key={opt.code} value={opt.code}>
                  {opt.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => onConfirm(choice)}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
            >
              {t(lang, 'receiptLang.start')}
            </button>
          </div>
        ) : (
          <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => setPicking(true)}
              className="rounded-md px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
            >
              {t(lang, 'receiptLang.chooseOther')}
            </button>
            <button
              type="button"
              autoFocus
              onClick={() => onConfirm(lang)}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
            >
              {t(lang, 'receiptLang.confirmSame').replace('{language}', uiLangLabel)}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
